import React, { useEffect, useRef, useState } from "react";
import "./processSection.css";

const ProcessSection = () => {
    const processSlides = [
        {
            title: "BRIEFING",
            description:
                "We begin by understanding your style, preferences, & needs. This initial consultation allows us to align our vision with yours, ensuring we create a space that reflects your personality and lifestyle.",
            image: "/images/briefing.png",
        },
        {
            title: "DESIGN",
            description:
                "Our design phase involves creative brainstorming, concept creation, and detailed planning to ensure the final outcome is visually stunning and functional.",
            image: "/images/design.png",
        },
        {
            title: "EXECUTION",
            description:
                "During the execution phase, our skilled team brings the design to life with precision, quality materials, and expert craftsmanship.",
            image: "/images/execution.png",
        },
        {
            title: "HANDOVER",
            description:
                "Finally, we complete the project with a thorough handover, ensuring every detail meets your expectations, and provide after-service support.",
            image: "/images/handover.png",
        },
    ];

    const [activeIndex, setActiveIndex] = useState(0);
    // released: when true, the container scrolls normally
    const [released, setReleased] = useState(false);

    const containerRef = useRef(null);
    const markerRef = useRef(null);
    const scrollAccumulator = useRef(0);
    const scrollTimeout = useRef(null);
    const threshold = 100; // Adjust scroll and swipe sensitivity as needed

    // Using a ref to keep the latest activeIndex for event handlers.
    const activeIndexRef = useRef(activeIndex);
    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    // IntersectionObserver to watch the marker element
    useEffect(() => {
        const checkVisibility = () => {
            const container = containerRef.current;
            if (!container) return;
    
            const rect = container.getBoundingClientRect();
            const isFullyVisible =
                rect.top === 0 &&
                rect.left === 0 &&
                rect.bottom === window.innerHeight &&
                rect.width === window.innerWidth;
    
            // Lock scroll only if fully visible (edge to edge)
            setReleased(!isFullyVisible);
        };
    
        window.addEventListener("scroll", checkVisibility);
        window.addEventListener("resize", checkVisibility);
        checkVisibility();
    
        return () => {
            window.removeEventListener("scroll", checkVisibility);
            window.removeEventListener("resize", checkVisibility);
        };
    }, []);

    // Wheel event handler for desktop scroll behavior
    useEffect(() => {
        const container = containerRef.current;
        const wheelHandler = (e) => {
            if (released) return; // ✅ Block scroll logic when not fully focused
        
            // Scroll behavior logic
            if (activeIndexRef.current === processSlides.length - 1 && e.deltaY > 0) {
                setReleased(true);
                return;
            }
        
            if (activeIndexRef.current === 0 && e.deltaY < 0) {
                setReleased(true);
                return;
            }
        
            setReleased(false);
            e.preventDefault();
        
            scrollAccumulator.current += e.deltaY;
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
            scrollTimeout.current = setTimeout(() => {
                scrollAccumulator.current = 0;
            }, 100);
        
            if (
                scrollAccumulator.current >= threshold &&
                activeIndexRef.current < processSlides.length - 1
            ) {
                setActiveIndex((prev) => prev + 1);
                scrollAccumulator.current = 0;
            } else if (
                scrollAccumulator.current <= -threshold &&
                activeIndexRef.current > 0
            ) {
                setActiveIndex((prev) => prev - 1);
                scrollAccumulator.current = 0;
            }
        };

        container.addEventListener("wheel", wheelHandler, { passive: false });
        return () => {
            container.removeEventListener("wheel", wheelHandler, { passive: false });
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
    }, [processSlides.length]);

    // Touch event handlers for mobile swipe support
    useEffect(() => {
        const container = containerRef.current;
        let touchStartY = null;
        let touchDeltaY = 0;

        const touchStartHandler = (e) => {
            if (e.touches.length === 1) {
                touchStartY = e.touches[0].clientY;
            }
        };

        const touchMoveHandler = (e) => {
            if (released || touchStartY === null) return; // ✅ Block swipe when not fully focused
        
            const currentY = e.touches[0].clientY;
            touchDeltaY = touchStartY - currentY;
        
            if (
                activeIndexRef.current === processSlides.length - 1 &&
                touchDeltaY > 0
            ) {
                setReleased(true);
                return;
            }
            if (activeIndexRef.current === 0 && touchDeltaY < 0) {
                setReleased(true);
                return;
            }
        
            setReleased(false);
            e.preventDefault(); // Lock normal page scroll
        };

        const touchEndHandler = () => {
            if (touchStartY === null) return;
            if (
                touchDeltaY >= threshold &&
                activeIndexRef.current < processSlides.length - 1
            ) {
                setActiveIndex((prev) => prev + 1);
            } else if (touchDeltaY <= -threshold && activeIndexRef.current > 0) {
                setActiveIndex((prev) => prev - 1);
            }
            // Reset touch values
            touchStartY = null;
            touchDeltaY = 0;
        };

        container.addEventListener("touchstart", touchStartHandler, { passive: false });
        container.addEventListener("touchmove", touchMoveHandler, { passive: false });
        container.addEventListener("touchend", touchEndHandler, { passive: false });

        return () => {
            container.removeEventListener("touchstart", touchStartHandler, { passive: false });
            container.removeEventListener("touchmove", touchMoveHandler, { passive: false });
            container.removeEventListener("touchend", touchEndHandler, { passive: false });
        };
    }, [processSlides.length]);

    return (
        <div className="wrapper">
            <div ref={markerRef} className="scroll-marker"></div>
            <div
                ref={containerRef}
                className={`process-container ${!released ? "fixed" : "released"}`}
            >
                <div className="process-component-info-container">
                    <p className="process-text">OUR PROCESS</p>
                    <h2 className="process-title">Our Proven Design Approach</h2>
                    <p className="process-subtitle">
                        Collaborative, Efficient, and Tailored to Your Needs
                    </p>
                </div>

                <div className="process-wheel-conatiner">
                    <div className="title-container">
                        {processSlides.map((slide, index) => (
                            <div key={index} className="process-title-conatiner">
                                <div className="proccess-title-bullet-container">
                                    <div
                                        className={
                                            activeIndex === index
                                                ? "process-title-bullet-active"
                                                : "process-title-bullet"
                                        }
                                    ></div>
                                    <div>
                                        <p
                                            className={
                                                activeIndex === index ? "active" : "non-active-title"
                                            }
                                        >
                                            {slide.title}
                                        </p>
                                    </div>
                                </div>
                                {index !== processSlides.length - 1 && (
                                    <div className="connection-line"></div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="image-description-container ">
                       
                            <p className="process-description">
                                {processSlides[activeIndex].description}
                            </p>
                            <img
                                className="process-image"
                                src={processSlides[activeIndex].image}
                                alt={processSlides[activeIndex].title}
                            />
                       
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcessSection;

